import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { BookOpenText, RefreshCw, Trash2, ChevronLeft, ChevronRight, Calendar as CalendarIcon, IndianRupee, Users, Wheat, Sparkles, FileText, LayoutGrid, List } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { formatCurrency } from "../lib/mdmData";

// Colour-coded status token used across the history views
function entryStatus(e) {
  if (!e) return { key: "none", label: "Not entered", cls: "bg-slate-100 text-slate-500 border-slate-200", dot: "⚪" };
  if ((e.entry_type || "meal") === "ladoo") return { key: "ladoo", label: "Ladoo", cls: "bg-orange-100 text-orange-800 border-orange-200", dot: "🧁" };
  if (e.day_type === "HOLIDAY") return { key: "holiday", label: "Holiday", cls: "bg-red-100 text-red-800 border-red-200", dot: "🔴" };
  if (e.meal_type === "DRY_RATION") return { key: "dry", label: "Dry Ration", cls: "bg-sky-100 text-sky-800 border-sky-200", dot: "🔵" };
  return { key: "mdm", label: "MDM", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "🟢" };
}

export default function DailyHistory() {
  const nav = useNavigate();
  const { user, loading, isAuthed } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [monthIndex, setMonthIndex] = useState(now.getMonth());
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sub, setSub] = useState(null);
  const [mode, setMode] = useState("list"); // "list" | "calendar"

  const { from_date, to_date, monthLabel } = useMemo(() => {
    const from = new Date(year, monthIndex, 1);
    const to = new Date(year, monthIndex + 1, 0);
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return { from_date: iso(from), to_date: iso(to), monthLabel: format(from, "MMMM yyyy") };
  }, [year, monthIndex]);

  useEffect(() => {
    if (!loading && !isAuthed) nav("/login");
  }, [loading, isAuthed, nav]);

  useEffect(() => {
    if (!isAuthed) return;
    api.subMe().then(setSub).catch(() => {});
  }, [isAuthed]);

  const load = async () => {
    setBusy(true);
    try {
      const rows = await api.listDailyEntries({ from_date, to_date });
      setEntries(rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
    } finally { setBusy(false); }
  };

  useEffect(() => { if (isAuthed) load(); }, [isAuthed, from_date, to_date]);   // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    let students = 0, cost = 0, rice = 0, days = 0, ladoos = 0;
    entries.forEach((e) => {
      const s = e.summary || {};
      students += Number(s.total_students || 0);
      cost += Number(s.grand_total || 0);
      rice += Number(s.total_rice_kg || 0);
      days += 1;
      if ((e.entry_type || "meal") === "ladoo") ladoos += Number(s.total_students || 0);
    });
    return { students, cost, rice, days, ladoos };
  }, [entries]);

  const shift = (delta) => {
    let m = monthIndex + delta;
    let y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setYear(y); setMonthIndex(m);
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.deleteDailyEntry(deleteTarget.date_iso, deleteTarget.entry_type || "meal");
      setEntries((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      toast.success("Entry removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setDeleteTarget(null); }
  };

  if (loading || !user) return <div className="p-10 text-center text-slate-500">Loading…</div>;
  const premium = !!sub?.is_premium;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="daily-history-page">
      <div>
        <div className="chip chip-green"><BookOpenText size={14}/> Daily history</div>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Your saved entries</h1>
        <p className="mt-1 text-slate-600">Every day you&rsquo;ve saved, month by month.</p>
      </div>

      {!premium && (
        <div className="mt-4 rounded-2xl border-2 border-orange-300 bg-orange-50 p-4 flex items-center justify-between gap-3" data-testid="history-paywall">
          <div className="flex items-center gap-2"><Sparkles size={16} className="text-orange-700"/><span className="text-sm text-orange-900">Save entries with Premium (₹299/year · Launch Offer) to build your history.</span></div>
          <Button onClick={() => nav("/premium")} className="bg-orange-600 hover:bg-orange-700 text-white">Unlock</Button>
        </div>
      )}

      {/* Month nav + KPIs */}
      <div className="mt-6 grid gap-3 sm:grid-cols-5">
        <div className="sm:col-span-5 flex items-center justify-between gap-2">
          <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shift(-1)} data-testid="history-prev-month"><ChevronLeft size={16}/></Button>
          <div className="flex-1 text-center font-display font-bold text-lg text-slate-900" data-testid="history-month-label"><CalendarIcon size={14} className="inline text-emerald-700 mr-1"/> {monthLabel}</div>
          <Button variant="outline" size="icon" className="h-11 w-11" onClick={() => shift(1)} data-testid="history-next-month"><ChevronRight size={16}/></Button>
        </div>
        <Kpi icon={<CalendarIcon size={16}/>} label="Days" value={totals.days} testid="history-kpi-days"/>
        <Kpi icon={<Users size={16}/>} label="Students" value={totals.students} tint="emerald" testid="history-kpi-students"/>
        <Kpi icon={<span className="text-sm">🧁</span>} label="Ladoos" value={totals.ladoos} tint="orange" testid="history-kpi-ladoos"/>
        <Kpi icon={<IndianRupee size={16}/>} label="Cost" value={formatCurrency(totals.cost)} tint="orange" testid="history-kpi-cost"/>
        <Kpi icon={<Wheat size={16}/>} label="Rice" value={`${totals.rice.toFixed(2)} kg`} tint="emerald" testid="history-kpi-rice"/>
      </div>

      {/* View mode: List vs Calendar */}
      <div className="mt-4">
        <Tabs value={mode} onValueChange={setMode} className="w-full">
          <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-2" data-testid="history-view-tabs">
            <TabsTrigger value="list" data-testid="history-tab-list"><List size={12}/> List</TabsTrigger>
            <TabsTrigger value="calendar" data-testid="history-tab-calendar"><LayoutGrid size={12}/> Calendar</TabsTrigger>
          </TabsList>

          <TabsContent value="calendar" className="mt-3">
            <MonthCalendar year={year} monthIndex={monthIndex} entries={entries} onPick={() => nav("/daily-entry")}/>
          </TabsContent>

          <TabsContent value="list" className="mt-3">
            {/* Table */}
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="font-display font-bold text-slate-900">Entries · {monthLabel}</div>
          <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="history-reload"><RefreshCw size={12}/> {busy ? "Loading…" : "Reload"}</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Date</Th><Th>Day</Th><Th>Type</Th><Th>Menu</Th>
                <Th className="text-center">Students</Th><Th className="text-right">Rice (kg)</Th>
                <Th className="text-right">Cost (₹)</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-slate-500" data-testid="history-empty">
                  No entries in this month. {premium ? "Save today's report on Daily Entry to start." : "Unlock Premium to start saving."}
                </td></tr>
              )}
              {entries.map((e) => {
                const isLadoo = (e.entry_type || "meal") === "ladoo";
                const status = entryStatus(e);
                return (
                  <tr key={e.id} className={`border-b border-slate-100 hover:bg-slate-50 ${isLadoo ? "bg-orange-50/40" : status.key === "holiday" ? "bg-red-50/40" : status.key === "dry" ? "bg-sky-50/40" : ""}`} data-testid={`history-row-${e.id}`}>
                    <Td className="font-semibold text-slate-900">{format(parseISO(e.date_iso), "d MMM")}</Td>
                    <Td>{e.weekday_name}</Td>
                    <Td><span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${status.cls}`}>{status.dot} {status.label}</span></Td>
                    <Td className="text-slate-600 max-w-[240px] truncate" title={e.menu_display}>{e.menu_display}</Td>
                    <Td className="text-center font-semibold">{e.summary?.total_students || 0}</Td>
                    <Td className="text-right">{Number(e.summary?.total_rice_kg || 0).toFixed(3)}</Td>
                    <Td className="text-right font-semibold text-slate-900">{formatCurrency(e.summary?.grand_total || 0)}</Td>
                    <Td className="text-right">
                      <button onClick={() => setDeleteTarget(e)} className="text-slate-400 hover:text-red-600" data-testid={`history-del-${e.id}`}>
                        <Trash2 size={14}/>
                      </button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
            {entries.length > 0 && (
              <tfoot>
                <tr className="bg-emerald-50">
                  <Td colSpan={4} className="font-bold text-emerald-900 text-right">Month total</Td>
                  <Td className="text-center font-bold text-emerald-900">{totals.students}</Td>
                  <Td className="text-right font-bold text-emerald-900">{totals.rice.toFixed(2)}</Td>
                  <Td className="text-right font-display font-extrabold text-lg text-emerald-800" data-testid="history-grand-total">{formatCurrency(totals.cost)}</Td>
                  <Td></Td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="history-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete this entry?</DialogTitle>
            <DialogDescription>Removes the {deleteTarget?.entry_type === "ladoo" ? "Ladoo" : "meal"} entry for <b>{deleteTarget?.date_iso}</b>. You can re-save it any time.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button onClick={doDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="history-confirm-delete">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ icon, label, value, tint, testid }) {
  const bg = tint === "emerald" ? "border-emerald-200 bg-emerald-50" : tint === "orange" ? "border-orange-200 bg-orange-50" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border p-3 ${bg}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1">{icon}{label}</div>
      <div className="mt-1 font-display font-extrabold text-lg text-slate-900">{value}</div>
    </div>
  );
}
const Th = ({ children, className = "" }) => <th className={`p-3 text-xs uppercase tracking-wider font-bold text-slate-500 ${className}`}>{children}</th>;
const Td = ({ children, className = "", colSpan }) => <td colSpan={colSpan} className={`p-3 text-slate-700 ${className}`}>{children}</td>;

function MonthCalendar({ year, monthIndex, entries, onPick }) {
  // Build a map keyed by yyyy-mm-dd for lookup. Prefer `meal` over `ladoo` when both exist.
  const byDate = useMemo(() => {
    const m = new Map();
    entries.forEach((e) => {
      const prev = m.get(e.date_iso);
      if (!prev || (e.entry_type || "meal") === "meal") m.set(e.date_iso, e);
    });
    return m;
  }, [entries]);

  const firstDay = new Date(year, monthIndex, 1);
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  // Monday-first grid — 0 = Mon … 6 = Sun
  const leading = (firstDay.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < leading; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const iso = (d) => `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="history-calendar">
      <div className="grid grid-cols-7 gap-2 text-[10px] uppercase tracking-widest font-bold text-slate-500">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => <div key={d} className="text-center">{d}</div>)}
      </div>
      <div className="mt-2 grid grid-cols-7 gap-2">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="h-16 rounded-lg bg-slate-50/50"/>;
          const entry = byDate.get(iso(d));
          const status = entryStatus(entry);
          return (
            <button key={i} onClick={onPick}
              className={`h-16 rounded-lg border text-left p-1.5 hover:shadow transition-all flex flex-col justify-between ${status.cls}`}
              data-testid={`cal-cell-${iso(d)}`}>
              <div className="text-xs font-bold">{d}</div>
              <div className="text-[10px] font-semibold flex items-center gap-1">
                <span>{status.dot}</span>
                <span className="truncate">{entry ? status.label : ""}</span>
              </div>
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap gap-2 text-[11px] text-slate-600">
        <Legend dot="🟢" label="MDM"/>
        <Legend dot="🔵" label="Dry Ration"/>
        <Legend dot="🔴" label="Holiday"/>
        <Legend dot="🧁" label="Ladoo"/>
        <Legend dot="⚪" label="Not entered"/>
      </div>
    </div>
  );
}

function Legend({ dot, label }) {
  return <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5">{dot} {label}</span>;
}
